// Centralizes the "look up a raw print method/position/color value in the
// product config's Magento-translated maps, fall back to the raw value
// unchanged" pattern used at every render site that shows one of these
// values to the customer (DesignerHeader pills, ProductOptionsModal chips,
// BackgroundColorTool's Select Colors step). Display-layer only — every
// internal matching/filtering path keeps comparing against the raw,
// untranslated value, exactly as before this helper existed.
import type { ProductDesignerConfig } from '@customedge/shared';
import { FULL_COLOR, printColorOptionToString, type PrintColorOption } from '@customedge/shared';

// productConfig may be null/undefined during initial load — every call site
// already has to handle that for the raw fields, so this mirrors the same
// "no config yet" -> pass raw value through unchanged behavior.
type TranslatableConfig = Pick<
  ProductDesignerConfig,
  'printMethodsTranslated' | 'printPositionsTranslated' | 'printColorOptionsTranslated'
> | null | undefined;

export function translatedMethodLabel(productConfig: TranslatableConfig, method: string | null | undefined): string | undefined {
  if (!method) return undefined;
  return productConfig?.printMethodsTranslated?.[method] ?? method;
}

export function translatedPositionLabel(productConfig: TranslatableConfig, position: string | null | undefined): string | undefined {
  if (!position) return undefined;
  return productConfig?.printPositionsTranslated?.[position] ?? position;
}

// method is required to resolve a color-option's translation (the raw-key
// convention is "{method} {level}", see translationLookup.ts) — without a
// method, there's nothing to key the lookup on, so this returns the plain
// stringified option (e.g. "2", or the localized "Full color" via
// fullColorLabel) exactly as every render site already did before
// translation existed.
export function translatedColorOptionLabel(
  productConfig: TranslatableConfig,
  method: string | null | undefined,
  opt: PrintColorOption,
  fullColorLabel: string,
): string {
  if (opt === FULL_COLOR) {
    // No universal raw-key convention for "full color" across vendors (see
    // translationLookup.ts's own comment) — only PFConcept-shaped "DTF
    // Transfer" methods have a real level-0 "Full-colour" translation, and
    // even that is method-specific, not a fixed shared string. Keep the
    // existing localized fullColorLabel here rather than guess.
    return fullColorLabel;
  }
  const key = printColorOptionToString(opt);
  if (method && productConfig?.printColorOptionsTranslated?.[method]?.[key]) {
    return productConfig.printColorOptionsTranslated[method][key];
  }
  return key;
}
